import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { api } from '../lib/api-client';
import {
  APPEARANCE_KEY_DENSITY,
  APPEARANCE_KEY_THEME,
  APPEARANCE_NAMESPACE,
  applyAppearance,
  resolveTheme,
  type DensityChoice,
  type ResolvedTheme,
  type ThemeChoice,
} from '../lib/appearance';
import { useAppearanceStore } from '../stores/appearance-store';
import { useAuthStore } from '../stores/auth-store';
import { useToastStore } from '../stores/toast-store';

const MEDIA_QUERY = '(prefers-color-scheme: dark)';

function hasMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function getPrefersDark(): boolean {
  if (!hasMatchMedia()) return false;
  return window.matchMedia(MEDIA_QUERY).matches;
}

const NOOP_UNSUBSCRIBE = (): void => undefined;

function subscribePrefersDark(onChange: () => void): () => void {
  if (!hasMatchMedia()) return NOOP_UNSUBSCRIBE;
  const mql = window.matchMedia(MEDIA_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/**
 * `true` quando o sistema operacional está em modo escuro.
 *
 * `useSyncExternalStore` em vez de `useState` + `useEffect` porque a fonte é
 * externa ao React: quem escolheu «system» precisa ver a tela virar quando o
 * SO vira à noite, sem clicar em nada. O fallback é `false` (claro) — e é
 * exercitado de verdade, não hipotético: `window.matchMedia` **não existe no
 * jsdom** (medido), e também não existe em SSR.
 */
export function usePrefersDark(): boolean {
  return useSyncExternalStore(subscribePrefersDark, getPrefersDark, () => false);
}

/**
 * Monta UMA vez no shell da aplicação. Faz três coisas e nada mais:
 *
 *  1. escreve `data-theme` / `data-density` no `<html>` a cada mudança;
 *  2. reage ao SO virar, quando a escolha é «system»;
 *  3. carrega a preferência do servidor (namespace `ui`) quando há sessão —
 *     é o que faz a escolha atravessar de um computador para outro.
 *
 * O fetch é GATEADO no `enabled`, não só no render: sem sessão não há chamada
 * (um GET sem token devolveria 401 e sujaria o console em toda tela de login).
 */
export function useAppearanceEffect(): void {
  const theme = useAppearanceStore((s) => s.theme);
  const density = useAppearanceStore((s) => s.density);
  const hydrateFromServer = useAppearanceStore((s) => s.hydrateFromServer);
  const accessToken = useAuthStore((s) => s.accessToken);
  const prefersDark = usePrefersDark();

  const { data } = useQuery({
    queryKey: ['user-preferences', APPEARANCE_NAMESPACE],
    queryFn: () => api.getUserPreferenceNamespace(APPEARANCE_NAMESPACE),
    enabled: accessToken !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    if (data === undefined) return;
    hydrateFromServer(data);
  }, [data, hydrateFromServer]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    applyAppearance(document.documentElement, { theme, density }, prefersDark);
  }, [theme, density, prefersDark]);
}

export interface UseAppearanceResult {
  /** A ESCOLHA do operador — pode ser `system`. */
  theme: ThemeChoice;
  /** O tema que está de fato na tela — nunca `system`. */
  resolvedTheme: ResolvedTheme;
  density: DensityChoice;
  setTheme: (next: ThemeChoice) => void;
  setDensity: (next: DensityChoice) => void;
}

/**
 * Hook da UI. A escolha é aplicada e gravada localmente de forma SÍNCRONA; a
 * ida ao servidor é best-effort e nunca reverte o que o operador escolheu —
 * um 403 ou um servidor fora do ar degradam a preferência para «só neste
 * navegador», e o operador é avisado disso uma vez, em vez de a sessão fingir
 * que salvou.
 */
export function useAppearance(): UseAppearanceResult {
  const theme = useAppearanceStore((s) => s.theme);
  const density = useAppearanceStore((s) => s.density);
  const setThemeLocal = useAppearanceStore((s) => s.setTheme);
  const setDensityLocal = useAppearanceStore((s) => s.setDensity);
  const prefersDark = usePrefersDark();

  const push = useCallback((key: string, value: string) => {
    void api.setUserPreference(APPEARANCE_NAMESPACE, key, value).catch(() => {
      // Avisa UMA vez por sessão — repetir a cada clique seria ruído numa tela
      // de trabalho de 8h; calar de vez seria fingir que salvou.
      if (useAppearanceStore.getState().serverPersistWarned) return;
      useAppearanceStore.getState().markServerPersistWarned();
      useToastStore.getState().push({
        variant: 'info',
        title: 'Aparência aplicada só neste navegador',
        description:
          'Não foi possível salvar a preferência no seu perfil — ela não vai acompanhar você em outro computador.',
      });
    });
  }, []);

  const setTheme = useCallback(
    (next: ThemeChoice) => {
      setThemeLocal(next);
      push(APPEARANCE_KEY_THEME, next);
    },
    [setThemeLocal, push],
  );

  const setDensity = useCallback(
    (next: DensityChoice) => {
      setDensityLocal(next);
      push(APPEARANCE_KEY_DENSITY, next);
    },
    [setDensityLocal, push],
  );

  return {
    theme,
    resolvedTheme: resolveTheme(theme, prefersDark),
    density,
    setTheme,
    setDensity,
  };
}
